// Components/Services/FullHomeRemodel/HomeRemodelProcess.jsx
import React from "react";
import { motion } from "framer-motion";
import { FiCompass, FiUsers, FiSettings, FiTool, FiCheckCircle } from "react-icons/fi";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const stepVariants = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const steps = [
  {
    number: "01",
    title: "Consultation & Scope Definition",
    desc: "We walk through your home with you, listen to your goals, and define a clear scope of work with a realistic budget and timeline — before any contracts are signed.",
    icon: <FiCompass className="w-5 h-5 text-white" />,
  },
  {
    number: "02",
    title: "Design Selection & Material Planning",
    desc: "We guide you through finish selections, fixtures, tile, cabinetry, and materials — coordinating with designers if needed to ensure everything works together.",
    icon: <FiUsers className="w-5 h-5 text-white" />,
  },
  {
    number: "03",
    title: "Permits & Pre-Construction",
    desc: "We pull all required permits, finalize subcontractor schedules, and prepare the site — so construction starts on day one without delays.",
    icon: <FiSettings className="w-5 h-5 text-white" />,
  },
  {
    number: "04",
    title: "Construction & Quality Control",
    desc: "Demo, framing, MEP rough-in, insulation, drywall, tile, cabinetry, paint, and trim — every phase executed by our crew with milestone updates along the way.",
    icon: <FiTool className="w-5 h-5 text-white" />,
  },
  {
    number: "05",
    title: "Final Walkthrough & Handoff",
    desc: "We complete a detailed punch-list walkthrough with you, address every item, pass all inspections, and hand over a finished home you're proud of.",
    icon: <FiCheckCircle className="w-5 h-5 text-white" />,
  },
];

const HomeRemodelProcess = () => {
  return (
    <section className="bg-white px-6 pt-10 pb-10 md:pt-14 md:pb-14">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-[#6c757d] uppercase tracking-wider text-xs font-semibold mb-1">
            How We Work
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-[#212529]">
            Our Remodel Process
          </h2>
          <p className="text-[#6c757d] text-sm max-w-xl mx-auto mt-2">
            A structured, transparent process from first call to final walkthrough — no surprises, no hand-offs, no guesswork.
          </p>
        </motion.div>

        {/* Dark Card */}
        <motion.div
          className="bg-[#212529] rounded-2xl overflow-hidden flex flex-col md:flex-row shadow-2xl"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65 }}
        >
          {/* Left — image */}
          <div className="md:w-[36%] w-full flex-shrink-0 p-4">
            <img
              src="/Images/full-house-remodel.png"
              alt="Full home remodel process"
              className="w-full h-full object-cover rounded-xl"
              style={{ minHeight: "320px", maxHeight: "460px" }}
            />
          </div>

          {/* Right — steps */}
          <div className="flex-1 flex flex-col justify-between px-6 py-5 md:px-8 md:py-6">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="flex flex-col"
            >
              {steps.map((step, idx) => (
                <motion.div
                  key={idx}
                  variants={stepVariants}
                  className="flex items-center gap-4 px-3 py-2.5 rounded-xl cursor-default
                             transition-all duration-300 ease-in-out
                             hover:bg-white/[0.06] hover:scale-[1.015] hover:shadow-[0_0_0_1px_rgba(255,255,255,0.08)]
                             group"
                >
                  <span className="text-2xl md:text-3xl font-bold text-white/20 w-10 flex-shrink-0 leading-none
                                   group-hover:text-white/50 transition-colors duration-300">
                    {step.number}
                  </span>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold text-sm md:text-base leading-snug
                                   group-hover:text-white transition-colors duration-300">
                      {step.title}
                    </h3>
                    <p className="text-white/45 text-xs md:text-sm mt-0.5 leading-relaxed
                                  group-hover:text-white/70 transition-colors duration-300">
                      {step.desc}
                    </p>
                  </div>

                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center
                                  group-hover:bg-white/20 group-hover:scale-110 transition-all duration-300">
                    {step.icon}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Bottom CTA */}
            <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-4 border-t border-white/10">
              <p className="text-white/55 text-xs max-w-xs leading-relaxed">
                Ready to transform your home?{" "}
                <span className="text-white font-medium">Let's build something great together.</span>
              </p>
              <a
                href="/contact"
                className="inline-block bg-white text-[#212529] text-xs font-semibold px-5 py-2.5 rounded-full
                           hover:bg-[#f1f3f5] hover:shadow-md transition-all duration-200 whitespace-nowrap"
              >
                Get a Free Estimate
              </a>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default HomeRemodelProcess;